import React, { useState } from "react";
import "./style.scss";
import editIcon from "../../../assets/icons/edit-icon-white.svg";
import eyeIcon from "../../../assets/icons/eye-icon.svg";
import eyeOffIcon from "../../../assets/icons/eye-off-icon.svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import Spinner from "../../../reusableComponents/spinner/Spinner";
import { returnMessages } from "../../../utils/store/reducers/Message";
import { returnErrors } from "../../../utils/store/reducers/Error";
import { changePassword } from "../../../utils/apis/userApi";
import { useDispatch } from "react-redux";

const Password = () => {
  // Validation schema
  const validationSchema = Yup.object().shape({
    current_password: Yup.string()
      .required("Current Password is required")
      .min(6, "Password must be at least 6 characters"),
    new_password: Yup.string()
      .required("New Password is required")
      .min(6, "Password must be at least 6 characters"),
    reEnter_password: Yup.string()
      .required("Re-Enter Password is required")
      .oneOf([Yup.ref("new_password"), null], "Passwords must match"),
  });

  const [editMode, setEditMode] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showReEnterPassword, setShowReEnterPassword] = useState(false);
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      reEnter_password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          current_password: values.current_password,
          new_password: values.new_password,
        };
        const response = await changePassword(payload);

        if (response.status < 400) {
          dispatch(returnMessages("Password updated successfully!", 200));
          resetForm();
          setEditMode(true);
        } else {
          dispatch(returnErrors("Error updating password", 400));
        }
      } catch (error) {
        console.error("Error updating password:", error);
        dispatch(returnErrors("Error updating password", 400));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    formik.handleSubmit();
    setEditMode(false); // Switch to non-edit mode after form submission
  };

  return (
    <div className="password_container">
      <div className="password_wrapper">
        <div className="header_wrapper">
          <h2>Password</h2>
          {editMode ? (
            <button className="edit_button" onClick={() => setEditMode(false)}>
              Edit <img src={editIcon} alt="" />
            </button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button
                type="submit"
                className="submit_button"
                onClick={handleSubmit}
              >
                {formik.isSubmitting ? <Spinner size={30} stroke={3}/> : "Submit"}
              </button>
              <button
                className="edit_button"
                onClick={() => {
                  setEditMode(!editMode);
                  formik.resetForm();
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        <form onSubmit={handleSubmit} className="password_form">
          <div className="password_form_input">
            <label htmlFor="current_password" className="form_input_label">
              Current Password
            </label>
            <div className="form_input_box_wrapper">
              <input
                id="current_password"
                name="current_password"
                type={showCurrentPassword ? "text" : "password"}
                placeholder="Type Your Current Password"
                className="form_input_box"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.current_password}
                disabled={editMode}
              />
              <img
                src={showCurrentPassword ? eyeOffIcon : eyeIcon}
                alt="toggle visibility"
                onClick={() => {
                  if (!editMode) {
                    setShowCurrentPassword(!showCurrentPassword);
                  }
                }}
                className="toggle_visibility_icon"
              />
              {formik.touched.current_password &&
              formik.errors.current_password ? (
                <div className="error_message">
                  {formik.errors.current_password}
                </div>
              ) : null}
            </div>
          </div>
          <div className="password_form_input">
            <label htmlFor="new_password" className="form_input_label">
              New Password
            </label>
            <div className="form_input_box_wrapper">
              <input
                id="new_password"
                name="new_password"
                type={showNewPassword ? "text" : "password"}
                placeholder="Type Your New Password"
                className="form_input_box"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.new_password}
                disabled={editMode}
              />
              <img
                src={showNewPassword ? eyeOffIcon : eyeIcon}
                alt="toggle visibility"
                onClick={() => {
                  if (!editMode) {
                    setShowNewPassword(!showNewPassword);
                  }
                }}
                className="toggle_visibility_icon"
              />
              {formik.touched.new_password && formik.errors.new_password ? (
                <div className="error_message">
                  {formik.errors.new_password}
                </div>
              ) : null}
            </div>
          </div>
          <div className="password_form_input">
            <label htmlFor="reEnter_password" className="form_input_label">
              Re-Enter Password
            </label>
            <div className="form_input_box_wrapper">
              <input
                id="reEnter_password"
                name="reEnter_password"
                type={showReEnterPassword ? "text" : "password"}
                placeholder="Re-Enter your Password"
                className="form_input_box"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.reEnter_password}
                disabled={editMode}
              />
              <img
                src={showReEnterPassword ? eyeOffIcon : eyeIcon}
                alt="toggle visibility"
                onClick={() => {
                  if (!editMode) {
                    setShowReEnterPassword(!showReEnterPassword);
                  }
                }}
                className="toggle_visibility_icon"
              />
              {formik.touched.reEnter_password &&
              formik.errors.reEnter_password ? (
                <div className="error_message">
                  {formik.errors.reEnter_password}
                </div>
              ) : null}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Password;
